import {Component, OnInit} from '@angular/core';
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {Globals} from '../../../globals';
import {InternalEmailService} from '../../../services/messaging/internal-email.service';
import {InternalEmailDataModel} from '../../../models/communication/internal-email-data.model';
import {EnumValidValueItemModel} from '../../../models/enum/enum-valid-value-item.model';
import {StaticDataService} from '../../../services/static-data.service';
import {TdoUserConfigurationModel} from '../../../models/configuration/user/tdo-user-configuration.model';
import {UserService} from '../../../services/user.service';
import {AuthenticationService} from '../../../modules/auth/services/authentication.service';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {InternalMessagingFilterModel} from './models/internal-messaging-filter.model';
import {
  InternalMessagingPersonModel,
  InternalMessagingPersonSelectDialogComponent
} from '../internal-messaging-person-select-dialog/internal-messaging-person-select-dialog.component';
import {InternalEmailDestinationDataModel} from '../../../models/communication/internal-email-destination-data.model';
import {PatientDataModel} from '../../../models/patient/patient-data.model';
import {PatientService} from '../../../modules/patient/patient.service';
import {DialogService} from '../../../services/dialog.service';
import {NotesService} from '../../../services/notes.service';
import {
  TdoNoteAssociatedObject,
  TdoNoteSaveModel
} from '../../../main-interface/controls/tdo-notes/models/tdo-note-save-model';
import {ColorSchemesService} from '../../../modules/configuration/color-schemes/color-schemes.service';
import {ColorSchemeDataModel} from '../../../models/configuration/color-scheme-data.model';
import {ToothSelectorResponseModel} from '../../tdo-tooth-selector-dialog/models/tooth-selector-response.model';
import {MessagingService} from '../../../services/messaging.service';
import {MessagingConstants} from '../../../models/MessagingConstants';
import {NoteCategoryEnum} from '../../../models/enum/note-category.enum';
import {NoteTypeEnum} from '../../../models/enum/note-type.enum';

@Component({
  selector: 'app-internal-messaging-dialog',
  templateUrl: './internal-messaging-dialog.component.html',
  styleUrls: ['./internal-messaging-dialog.component.scss']
})
export class InternalMessagingDialogComponent implements OnInit {
  emails: InternalEmailDataModel[];

  sortEnums: EnumValidValueItemModel[] = [];
  priorityEnums: EnumValidValueItemModel[];
  typeEnums: EnumValidValueItemModel[];
  mailboxEnums: EnumValidValueItemModel[];

  users: TdoUserConfigurationModel[];
  filterModel = new InternalMessagingFilterModel();
  toUserModel = new InternalMessagingPersonModel();
  patientSelectModel = new InternalMessagingPersonModel();
  toUserFilterModel = new InternalMessagingPersonModel();
  fromUserFilterModel = new InternalMessagingPersonModel();

  patients: PatientDataModel[] = [];
  selectedSortEnum: EnumValidValueItemModel;
  colorSchemes: ColorSchemeDataModel[];

  constructor(private dialogRef: MatDialogRef<InternalMessagingDialogComponent>,
              private colorSchemeService: ColorSchemesService,
              private noteService: NotesService,
              private patientService: PatientService,
              private userService: UserService,
              private dialog: MatDialog,
              private globals: Globals,
              private staticDataService: StaticDataService,
              public authService: AuthenticationService,
              private dialogService: DialogService,
              private messagingService: MessagingService,
              private internalEmailService: InternalEmailService) { }

  ngOnInit(): void {
    if (this.staticDataService.isReady) {
      this.setupEnums();
    } else {
      this.setupEnums();
    }

  }

  setupEnums() {
    this.sortEnums = this.staticDataService.getValidValueItemsByValidValueName('enumIEmailSortOrder');
    this.typeEnums = this.staticDataService.getValidValueItemsByValidValueName('enumIEmailType');
    this.priorityEnums = this.staticDataService.getValidValueItemsByValidValueName('enumIEmailPriority');
    this.mailboxEnums = this.staticDataService.getValidValueItemsByValidValueName('enumIEmailBox');

    this.selectedSortEnum = this.sortEnums[0];

    this.userService.getUsers(this.globals.TDOCID).subscribe((users) => {
      this.users = users;
      this.patientService.getAllPatients(this.globals.LOCID).subscribe((res) => {
        this.patients = res;
        this.colorSchemeService.getColorSchemes().subscribe((schemes) => {
          this.colorSchemes = schemes;
          this.getEmails();
        });
      });
    });
  }

  getEmails() {
    this.internalEmailService.getInternalEmails(parseInt(this.globals.LOCID), this.filterModel).subscribe((res) => {
      this.emails = res;
      this.sortEmails();
    });
  }

  deleteEmails(ids: string[]) {
    this.internalEmailService.deleteInternalEmails(ids).subscribe(() => {
      this.emails = this.emails.filter(p => !ids.includes(p.IEmailID));
    });
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onOkClick() {
    this.dialogRef.close();
  }

  addNewMessage() {
    const model = new InternalEmailDataModel();
    model.SendDateTime = new Date(Date.now());
    model.LOCID = parseInt(this.globals.LOCID);
    model.SenderUserID = this.authService.currentUserValue.authdata.userid;
    model.PatientID = this.globals.currentPatientId;
    this.emails.unshift(model);
  }

  getFromUserName(email: InternalEmailDataModel) {
    const user = this.users.find(p => p.UserID === email.SenderUserID);
    if (user) {
      return user.UserName;
    }

    return null;
  }

  getDate(email: InternalEmailDataModel) {
    if (email.SendDateTime) {
      return new Date(email.SendDateTime).toLocaleString();
    }

    return null;
  }

  onTypeCheckBoxClick(event: MatCheckboxChange, enu: EnumValidValueItemModel) {
    if (event.checked) {
      if (!this.filterModel.types.includes(enu.ValidValueItemId)) {
        this.filterModel.types.push(enu.ValidValueItemId);
      }
    } else {
      const idx = this.filterModel.types.findIndex(p => p === enu.ValidValueItemId);
      if (idx > -1) {
        this.filterModel.types.splice(idx, 1);
      }
    }

    this.getEmails();
  }

  onPriorityCheckBoxClick(event: MatCheckboxChange, enu: EnumValidValueItemModel) {
    if (event.checked) {
      if (!this.filterModel.priorities.includes(enu.ValidValueItemId)) {
        this.filterModel.priorities.push(enu.ValidValueItemId);
      }
    } else {
      const idx = this.filterModel.priorities.findIndex(p => p === enu.ValidValueItemId);
      if (idx > -1) {
        this.filterModel.priorities.splice(idx, 1);
      }
    }

    this.getEmails();
  }

  onSelectPatientsForFilter(event: MatCheckboxChange) {
    if (event.checked) {
      this.patientSelectModel.mode = 'patient';
      this.patientSelectModel.patients = this.patients;
      const config = new MatDialogConfig();
      config.panelClass = 'dialog';
      config.data = this.patientSelectModel;
      this.dialog.open(InternalMessagingPersonSelectDialogComponent, config).afterClosed().subscribe((res) => {
        if (res) {
          this.filterModel.patientId = (res as InternalMessagingPersonModel).selectedPatient.PatientId;
          this.getEmails();
        }
      });
    } else {
      this.filterModel.patientId = null;
      this.getEmails();
    }
  }

  getPriorityEnum(email: InternalEmailDataModel) {
    const enu = this.priorityEnums.find(p => p.ValidValueItemId === email.enumIEmailPriorityID);
    return enu;
  }

  onAddRecipients(model: InternalEmailDataModel) {
    const mod = new InternalMessagingPersonModel();
    mod.mode = 'user';
    mod.users = this.users;
    const config = new MatDialogConfig();
    config.panelClass = 'dialog';
    config.data = mod;
    this.dialog.open(InternalMessagingPersonSelectDialogComponent, config).afterClosed().subscribe((res) => {
      if (res) {
        model.dataInternalEmailDestanations = [];
        const personDialogResponseModel = res as InternalMessagingPersonModel;
        for (const user of personDialogResponseModel.selectedUsers) {
          const destModel = new InternalEmailDestinationDataModel();
          destModel.UserID = user.UserID;
          model.dataInternalEmailDestanations.push(destModel);
        }
      }
    });
  }

  getRecipients(email: InternalEmailDataModel) {
    let ret = '';
    for (const dest of email.dataInternalEmailDestanations) {
      const destUser = this.users.find(p => p.UserID === dest.UserID);
      if (destUser) {
        if (ret.length === 0) {
          ret += destUser.UserName;
        } else {
          ret += ', ' + destUser.UserName;
        }
      }
    }
    return ret;
  }

  getTypeEnum(email: InternalEmailDataModel) {
    const type = this.typeEnums.find(p => p.ValidValueItemId === email.enumIEmailTypeID);
    if (type) {
      return type;
    }

    return null;
  }

  getPatient(email: InternalEmailDataModel) {
    let ret = '';
    const patient = this.patients.find(p => p.PatientId === email.PatientID);
    if (patient) {
      ret = '#' + patient.PatientNO + ' - ' + patient.dataContact.FirstName + ' ' + patient.dataContact.MidName + ' ' + patient.dataContact.LastName;
    }
    return ret;
  }

  onAddPatient(email: InternalEmailDataModel) {
    this.patientSelectModel.mode = 'patient';
    this.patientSelectModel.patients = this.patients;
    const config = new MatDialogConfig();
    config.panelClass = 'dialog';
    config.data = this.patientSelectModel;
    this.dialog.open(InternalMessagingPersonSelectDialogComponent, config).afterClosed().subscribe((res) => {
      if (res) {
        email.PatientID = (res as InternalMessagingPersonModel).selectedPatient.PatientId;
      }
    });
  }

  isCanSend(email: InternalEmailDataModel) {
    return email.dataInternalEmailDestanations.length > 0 && email.enumIEmailTypeID;
  }

  sendEmail(email: InternalEmailDataModel) {
    if (!this.isCanSend(email)) {
      return;
    }
    email.enumEmailBoxID = '00000197-1000-0000-0000-000000000002';
    this.internalEmailService.saveInternalEmail(email).subscribe((res) => {
      this.getEmails();
      //
    });
  }

  onTypeChange(event: any, email: InternalEmailDataModel) {
    const value = event.value as EnumValidValueItemModel;
    email.enumIEmailTypeID = value.ValidValueItemId;
  }

  onPriorityChange(event: any, email: InternalEmailDataModel) {
    const value = event.value as EnumValidValueItemModel;
    email.enumIEmailPriorityID = value.ValidValueItemId;
  }

  onMoveToSavedFolder(email: InternalEmailDataModel) {
    email.enumEmailBoxID = '00000271-1000-0000-0000-000000000002';

    this.internalEmailService.saveInternalEmail(email).subscribe((res) => {
      //
      this.getEmails();
    });
  }

  onMoveToDeleteFolder(email: InternalEmailDataModel) {
    email.enumEmailBoxID = '00000271-1000-0000-0000-000000000003';

    this.internalEmailService.saveInternalEmail(email).subscribe((res) => {
      //
      this.getEmails();
    });
  }

  onMoveAllToDeletedFolder() {
    this.internalEmailService.moveToFolder(this.emails.map(p => p.IEmailID), '00000271-1000-0000-0000-000000000003').subscribe(() => {
      //
      this.getEmails();
    });
  }

  onFromAllFilterChange(event: MatCheckboxChange) {
    if (event.checked) {
      this.filterModel.from = [];
      this.getEmails();
    }
  }

  onFromFilterChange(event: MatCheckboxChange) {
    if (event.checked) {
      this.fromUserFilterModel.mode = 'user';
      this.fromUserFilterModel.users = this.users;
      const config = new MatDialogConfig();
      config.panelClass = 'dialog';
      config.data = this.fromUserFilterModel;
      this.dialog.open(InternalMessagingPersonSelectDialogComponent, config).afterClosed().subscribe((res) => {
        if (res) {
          const personDialogResponseModel = res as InternalMessagingPersonModel;
          this.filterModel.from = personDialogResponseModel.selectedUsers.map(p => p.UserID);
          this.getEmails();
        }
      });
    } else {
      this.fromUserFilterModel.selectedUsers = [];
      this.filterModel.from = [];
      this.getEmails();
    }
  }

  onToAllFilterChange(event: MatCheckboxChange) {
    if (event.checked) {
      this.filterModel.to = [];
      this.getEmails();
    }
  }

  onToFilterChange(event: MatCheckboxChange) {
    if (event.checked) {
      this.toUserFilterModel.mode = 'user';
      this.toUserFilterModel.users = this.users;
      const config = new MatDialogConfig();
      config.panelClass = 'dialog';
      config.data = this.toUserFilterModel;
      this.dialog.open(InternalMessagingPersonSelectDialogComponent, config).afterClosed().subscribe((res) => {
        if (res) {
          const personDialogResponseModel = res as InternalMessagingPersonModel;
          this.filterModel.to = personDialogResponseModel.selectedUsers.map(p => p.UserID);
          this.getEmails();
        }
      });
    } else {
      this.toUserFilterModel.selectedUsers = [];
      this.filterModel.to = [];
      this.getEmails();
    }
  }

  createPatientNote(email: InternalEmailDataModel) {
    this.dialogService.openConfirmDialog('Would you like to select a case / teeth?', true).subscribe((toothConfirmationDialogRes) => {
      if (toothConfirmationDialogRes) {
        this.dialogService.openToothSelector(email.PatientID).subscribe((teethSelectionRes) => {
          const tr = teethSelectionRes as ToothSelectorResponseModel;
          const noteModel = new TdoNoteSaveModel();
          noteModel.text = new Date(tr.date).toDateString() + ' #';
          let i = 0;
          for (const t of tr.teeth) {
            if (i === 0) {
              noteModel.text += t;
            } else {
              noteModel.text += ', ' + t;
            }
            i++;
          }
          noteModel.text += '\r\n' + email.TextMessage;
          noteModel.patientId = email.PatientID;
          const assoc = new TdoNoteAssociatedObject();
          assoc.parentObjectName = 'dataPatientCase';
          assoc.objectId = tr.patientCaseId;
          noteModel.associatedObjects = [assoc];
          noteModel.typeEnumIds = [NoteTypeEnum.Patient, NoteTypeEnum.Administartive];
          this.noteService.saveNote(noteModel).subscribe((res1) => {
            this.messagingService.sendComponentMessage(MessagingConstants.NOTES_REFRESH, '');
          });
        });
      } else {
        if (toothConfirmationDialogRes === undefined || toothConfirmationDialogRes === null) {
          // Do nothing, they cancelled
        } else if (toothConfirmationDialogRes === false) {
          // They said no to selecting a case / teeth
          const noteModel = new TdoNoteSaveModel();
          noteModel.text += email.TextMessage;
          noteModel.patientId = email.PatientID;
          noteModel.associatedObjects = [];
          // noteModel.categoryEnumIds = [NoteCategoryEnum.];
          noteModel.typeEnumIds = [NoteTypeEnum.Patient, NoteTypeEnum.Administartive];
          this.noteService.saveNote(noteModel).subscribe((res1) => {

          });
        }
      }
    });


    /*const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'dialog';
    dialogConfig.data = new ToothPickerConfigModel();
    dialogConfig.data.isMultiSelect = true;
    const dialogRef = this.dialog.open(ToothPickerComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      // resolve(result);
      if (result === undefined) {
        return;
      }

      const models: ToothUiManagerModel[] = result;


      // procedure.ToothID = models[0].toothModel.ToothId;
      // procedure.toothModel = models[0].toothModel;


    });
    */
  }

  onReplyClick(email: InternalEmailDataModel) {
    const mod = new InternalEmailDataModel();
    mod.SenderUserID = this.authService.currentUserValue.authdata.userid;
    mod.TextMessage = '\r\n' + `/*${email.TextMessage}*/`;
    mod.PatientID = email.PatientID;
    mod.LOCID = email.LOCID;
    const modDest = new InternalEmailDestinationDataModel();
    modDest.UserID = email.SenderUserID;
    mod.dataInternalEmailDestanations.push(modDest);
    this.emails.unshift(mod);
  }

  onSortTypeChange(event: any) {
    const value = event.value as EnumValidValueItemModel;
    this.selectedSortEnum = value;
    this.sortEmails();
  }

  sortEmails() {
    switch (this.selectedSortEnum.ValidValueItemId) {
      case '00000270-1000-0000-0000-000000000001':
        // Date + Priority
        this.emails = this.emails.sort((a, b) => {
          const aDateTime = new Date(a.SendDateTime).getTime();
          const bDateTime = new Date(b.SendDateTime).getTime();
          if (aDateTime > bDateTime) {
            return -1;
          } else if (aDateTime < bDateTime) {
            return 1;
          } else {
            const aPriorEnum = this.priorityEnums.find(p => p.ValidValueItemId === a.enumIEmailPriorityID);
            const bPriorEnum = this.priorityEnums.find(p => p.ValidValueItemId === b.enumIEmailPriorityID);
            if (aPriorEnum.ItemOrder > bPriorEnum.ItemOrder) {
              return -1;
            } else {
              return 1;
            }
          }
          /*return aPriorEnum.ItemOrder - bPriorEnum.ItemOrder;*/
        });
        break;
      case '00000270-1000-0000-0000-000000000002':
        // Priority + Date
        this.emails = this.emails.sort((a, b) => {
          const aPriorEnum = this.priorityEnums.find(p => p.ValidValueItemId === a.enumIEmailPriorityID);
          const bPriorEnum = this.priorityEnums.find(p => p.ValidValueItemId === b.enumIEmailPriorityID);
          if (aPriorEnum.ItemOrder > bPriorEnum.ItemOrder) {
            return -1;
          } else if (aPriorEnum.ItemOrder < bPriorEnum.ItemOrder) {
            return 1;
          } else {
            const aDateTime = new Date(a.SendDateTime).getTime();
            const bDateTime = new Date(b.SendDateTime).getTime();
            if (aDateTime > bDateTime) {
              return -1;
            } else {
              return 1;
            }
          }
          /*return aPriorEnum.ItemOrder - bPriorEnum.ItemOrder;*/
        });
        break;
    }
  }

  onAllTypeChange(event: MatCheckboxChange) {
    if (event.checked) {
      this.filterModel.types = [];
      this.getEmails();
    }
  }

  onAllPrioritesFilterClick(event: MatCheckboxChange) {
    if (event.checked) {
      this.filterModel.priorities = [];
      this.getEmails();
    }
  }

  onAllPatientFilterClick(event: MatCheckboxChange) {
    if (event.checked) {
      this.filterModel.patientId = undefined;
      this.getEmails();
    }
  }

  onMailboxChange(event: MatCheckboxChange, enu: EnumValidValueItemModel) {
    if (event.checked) {
      if (!this.filterModel.mailbox.includes(enu.ValidValueItemId)) {
        this.filterModel.mailbox.push(enu.ValidValueItemId);
      }
    } else {
      const idx = this.filterModel.mailbox.findIndex(p => p === enu.ValidValueItemId);
      if (idx > -1) {
        this.filterModel.mailbox.splice(idx, 1);
      }
    }

    this.getEmails();
  }

  onSentFromMeFilterClick(event: MatCheckboxChange) {
    if (event.checked) {
      if (!this.filterModel.from.includes(this.authService.currentUserValue.authdata.userid)) {
        this.filterModel.from.push(this.authService.currentUserValue.authdata.userid);
      }
    } else {
      const idx = this.filterModel.from.findIndex(p => p === this.authService.currentUserValue.authdata.userid);
      if (idx > -1) {
        this.filterModel.from.splice(idx, 1);
      }
    }

    this.getEmails();
  }

  getUserNamesFromUserIds() {
    let ret = '';
    for (const id of this.filterModel.from) {
      const user = this.users.find(p => p.UserID === id);
      if (ret.length === 0) {
        ret += user.UserName;
      } else {
        ret += ', ' + user.UserName;
      }
    }
    return ret;
  }

  getPatientNameFromPatientId() {
    const patient = this.patients.find(p => p.PatientId === this.filterModel.patientId);
    if (patient) {
      return patient.PatientNO + ' - ' + patient.dataContact.FirstName + ' ' + patient.dataContact.LastName;
    }

    return '';
  }

  getBackgroundColor(email: InternalEmailDataModel) {
    const priorEnu = this.priorityEnums.find(p => p.ValidValueItemId === email.enumIEmailPriorityID);
    if (priorEnu) {
      const scheme = this.colorSchemes.find(p => p.ColorSchemeId === priorEnu.ColorSchemeId);
      if (scheme) {
        return scheme.BackColor;
      }
    }
  }
}
